import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Mic, Pause, Play, RotateCcw, Send, Square } from 'lucide-react'
import { useAudioRecorder } from '@/hooks/useAudioRecorder'
import { formatAudioTime, useAudioPlaybackTime } from '@/hooks/useAudioPlaybackTime'
import RecordingWaves from './RecordingWaves'

type RecorderViewState = 'idle' | 'preview' | 'submitted'

interface Props {
  onSubmit: (blob: Blob) => Promise<void>
  existingUrl?: string | null
  disabled?: boolean
  maxDurationSec?: number
  unavailableActionLabel?: string
  onUnavailableAction?: () => void
}

export default function WarmupAudioRecorder({
  onSubmit,
  existingUrl,
  disabled = false,
  maxDurationSec,
  unavailableActionLabel,
  onUnavailableAction,
}: Props) {
  const { t } = useTranslation('learner')
  const recorder = useAudioRecorder()
  const [viewState, setViewState] = useState<RecorderViewState>(
    existingUrl ? 'submitted' : 'idle',
  )
  const [starting, setStarting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const isRecording = recorder.isRecording
  const isFinished = viewState === 'preview' || viewState === 'submitted'
  const isSubmitted = viewState === 'submitted'
  const audioUrl = recorder.audioUrl ?? existingUrl ?? undefined
  const playback = useAudioPlaybackTime(audioUrl, recorder.elapsedSec)
  const stopRecording = recorder.stopRecording

  useEffect(() => {
    if (
      maxDurationSec != null &&
      isRecording &&
      recorder.elapsedSec >= maxDurationSec
    ) {
      stopRecording()
      setViewState('preview')
    }
  }, [isRecording, maxDurationSec, recorder.elapsedSec, stopRecording])

  async function handleStart() {
    if (disabled || starting) return
    setSubmitError(false)
    setStarting(true)
    await recorder.startRecording()
    setStarting(false)
  }

  function handleStop() {
    recorder.stopRecording()
    setViewState('preview')
  }

  function handleReset() {
    playback.resetPlayback()
    recorder.resetRecording()
    setSubmitError(false)
    setViewState('idle')
  }

  async function handleSubmit() {
    if (!recorder.audioBlob || submitting) return
    setSubmitError(false)
    setSubmitting(true)
    try {
      await onSubmit(recorder.audioBlob)
      setViewState('submitted')
    } catch {
      setSubmitError(true)
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePlayback() {
    await playback.togglePlayback()
  }

  const statusText = isRecording
    ? t('warmup.recorder.recording')
    : isSubmitted
      ? t('warmup.recorder.submitted')
      : isFinished
        ? t('warmup.recorder.completed')
        : t('warmup.recorder.idle')

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col items-center gap-6 pb-10 pt-4">
      <p
        role="status"
        className={`w-full text-center text-[16px] font-medium leading-[1.6] ${
          isRecording
            ? 'text-lc-text-error'
            : isSubmitted
              ? 'text-lc-text-success'
              : isFinished
                ? 'text-lc-text-link'
                : 'text-lc-text-tertiary'
        }`}
      >
        {statusText}
      </p>

      <div className="relative flex size-20 shrink-0 items-center justify-center">
        {isRecording ? <RecordingWaves /> : null}
        <button
          type="button"
          onClick={isRecording ? handleStop : isFinished ? handlePlayback : handleStart}
          disabled={disabled || starting || (isFinished && !audioUrl)}
          aria-label={
            isRecording
              ? t('warmup.recorder.stop')
              : isFinished
                ? playback.isPlaying
                  ? t('warmup.recorder.pause')
                  : t('warmup.recorder.play')
                : t('warmup.recorder.start')
          }
          className={`relative z-10 flex size-20 items-center justify-center rounded-full text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 disabled:cursor-not-allowed disabled:opacity-50 ${
            isRecording
              ? 'bg-lc-error-500 shadow-[0_6px_8px_0_rgb(255_103_94/0.4)] focus-visible:outline-lc-error-500'
              : 'bg-lc-key-500 shadow-[0_6px_8px_0_rgb(69_153_255/0.4)] hover:bg-lc-key-600 focus-visible:outline-lc-key-500'
          }`}
        >
          {isRecording ? (
            <Square className="size-[22px] fill-current stroke-none" />
          ) : isFinished ? (
            playback.isPlaying ? (
              <Pause className="size-7 fill-current stroke-none" />
            ) : (
              <Play className="ml-1 size-7 fill-current stroke-none" />
            )
          ) : (
            <Mic className="size-7" strokeWidth={2.4} />
          )}
        </button>

        {isSubmitted ? (
          <span className="absolute -right-1 -top-1 z-20 flex size-6 items-center justify-center rounded-full border-2 border-white bg-lc-success-500 text-white">
            <Check className="size-4" strokeWidth={3} />
          </span>
        ) : null}
      </div>

      <p
        className={`w-full text-center text-[18px] font-bold leading-[1.5] tabular-nums ${
          isRecording ? 'text-lc-text-error' : isFinished ? 'text-lc-key-500' : 'text-lc-text-disabled'
        }`}
      >
        {isFinished
          ? `${formatAudioTime(playback.currentSec)} / ${formatAudioTime(playback.durationSec)}`
          : formatAudioTime(recorder.elapsedSec)}
      </p>

      {viewState === 'preview' ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleReset}
            disabled={submitting}
            className="flex h-10 items-center justify-center gap-2 rounded-[10px] bg-lc-key-50 pl-3 pr-3.5 text-[14px] font-semibold text-lc-key-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500 disabled:opacity-50"
          >
            <RotateCcw className="size-4" />
            {t('warmup.recorder.reset')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !recorder.audioBlob}
            className="flex h-10 items-center justify-center gap-2 rounded-[10px] bg-lc-key-500 pl-3 pr-3.5 text-[14px] font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500 disabled:opacity-50"
          >
            <Send className="size-4" />
            {submitting ? t('warmup.recorder.submitting') : t('warmup.recorder.submit')}
          </button>
        </div>
      ) : null}

      {(recorder.error || submitError || (
        recorder.unavailableReason && unavailableActionLabel && onUnavailableAction
      )) ? (
        <div className="-mt-2 flex flex-col items-center gap-3">
          {recorder.error || submitError ? (
            <p role="alert" className="text-center text-[13px] text-lc-text-error">
              {submitError ? t('warmup.errors.submit') : t('warmup.errors.record')}
            </p>
          ) : null}
          {recorder.unavailableReason && unavailableActionLabel && onUnavailableAction ? (
            <button
              type="button"
              onClick={onUnavailableAction}
              className="min-h-10 rounded-[10px] border border-lc-key-300 bg-white px-4 text-[14px] font-semibold text-lc-key-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
            >
              {unavailableActionLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      {audioUrl ? (
        <audio
          ref={playback.audioRef}
          src={audioUrl}
          className="hidden"
          onLoadedMetadata={playback.handleLoadedMetadata}
          onTimeUpdate={playback.handleTimeUpdate}
          onPlay={playback.handlePlay}
          onPause={playback.handlePause}
          onEnded={playback.handleEnded}
        />
      ) : null}
    </div>
  )
}
